import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { logout } from '@/features/auth/services/authService'
import Button from '@/shared/components/Button/Button'
import ConfirmationModal from '@/shared/components/Modal/ConfirmationModal/ConfirmationModal'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import AccountAPI from '../api/AccountAPI'
import { SIGN_OUT_EVERYWHERE_USE_API } from '../config'
import { useSettingsModal } from '../store/useSettingsModal'

/**
 * Ends the account's sessions everywhere, this browser's included, after a
 * confirmation. Until the backend has the route (`config.ts`), only this
 * browser is signed out, and the confirmation says so: an owner who lost a
 * phone must not be told its session is over while it is not.
 */
function SignOutEverywhereField() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()
  const closeSettings = useSettingsModal(s => s.close)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)

  const signOutEverywhere = async () => {
    setIsConfirmOpen(false)
    setIsSigningOut(true)
    try {
      if (SIGN_OUT_EVERYWHERE_USE_API) await AccountAPI.revokeSessions()
      closeSettings()
      await logout()
      navigate('/login')
    } catch {
      setIsSigningOut(false)
      showSnackbar(
        t('ALERTS.SIGN_OUT_EVERYWHERE_ERROR.TITLE'),
        t('ALERTS.SIGN_OUT_EVERYWHERE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  }

  return (
    <>
      <Button
        label={t('SETTINGS.ACCOUNT.SIGN_OUT_EVERYWHERE_BUTTON')}
        variant="text"
        color="primary"
        icon="las la-sign-out-alt icon-medium"
        disabled={isSigningOut}
        onClick={() => setIsConfirmOpen(true)}
      />
      <ConfirmationModal
        isOpen={isConfirmOpen}
        title={t('SETTINGS.ACCOUNT.SIGN_OUT_EVERYWHERE_CONFIRM.TITLE')}
        message={t(
          SIGN_OUT_EVERYWHERE_USE_API
            ? 'SETTINGS.ACCOUNT.SIGN_OUT_EVERYWHERE_CONFIRM.MESSAGE'
            : 'SETTINGS.ACCOUNT.SIGN_OUT_EVERYWHERE_CONFIRM.MESSAGE_LOCAL'
        )}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={signOutEverywhere}
        // Opened from inside the settings modal, whose centring transform would
        // otherwise trap it in that box (Modal.tsx).
        disablePortal={false}
        // The global flag belongs to the page underneath.
        resetsFormDirty={false}
      />
    </>
  )
}

export default SignOutEverywhereField
